import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import { ReactEventHandler } from "react";
import { FormattedMessage } from "react-intl";

import { getStageConfig } from "@/helpers/stageConfig";
import { VideoFileConfig } from "@/types/common";
import { Day1EventCategory } from "@/types/snowplow-events";

export interface VideoProps {
  /*** The video config which contains filename and filetype ***/
  videoFileConfig: VideoFileConfig;

  /**
   * The i18n id of the overlay text.
   */
  overlayTextI18nId?: string;
}

/**
 * A video playback to guide/show NHs throughout the onBoarding steps.
 *
 * @constructor
 */
export default function Video({
  videoFileConfig,
  overlayTextI18nId,
}: VideoProps): JSX.Element {
  const cfnDomain = getStageConfig().publicAssetsCfnDomain;
  // We only support english for the MLP
  const locale = "en";

  const videoSrc =
    videoFileConfig.fileNameOrSrcUrl.fileSrcUrl ??
    `${cfnDomain}/${locale}/${videoFileConfig.fileNameOrSrcUrl.fileName}`;

  const handlePlayback =
    (baseAction: string): ReactEventHandler<HTMLVideoElement> =>
    (event) => {
      if (!videoFileConfig.metricActionSubject) {
        return;
      }
      const player = event.target as HTMLVideoElement;
      trackStructEvent({
        category: Day1EventCategory.VideoPlayback,
        action: `${baseAction}${videoFileConfig.metricActionSubject}Video`,
        label: player.currentTime.toFixed(1),
      });
    };

  const handlePlay = handlePlayback("played");
  const handlePause = handlePlayback("paused");
  const handleEnded = handlePlayback("completed");

  return (
    <Col>
      {overlayTextI18nId && (
        <Row justifyContent="flex-end">
          <View
            backgroundColor="color.link.primary-default"
            padding={{
              top: "S100",
              bottom: "S100",
              left: "S300",
              right: "S300",
            }}
            className="video-overlay-box"
          >
            <Text
              dataTestId="videoOverlayText"
              fontSize="T200"
              fontWeight="medium"
              color="color.text.primary-inverted"
            >
              <FormattedMessage id={overlayTextI18nId} />
            </Text>
          </View>
        </Row>
      )}
      <video
        // Always enable controls because of the accessibility issues for users with disabilities
        controls
        controlsList="noplaybackrate nodownload nofullscreen"
        disablePictureInPicture
        width="100%"
        height="100%"
        data-test-id="videoDataTestId"
        poster={videoFileConfig.poster}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
      >
        <source
          src={videoSrc}
          type={videoFileConfig.fileType}
          data-test-id="videoSourceDataTestId"
        />
        <Text fontWeight="regular">
          <FormattedMessage id="error-notSupportVideoTag" />
        </Text>
      </video>
    </Col>
  );
}
